import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { formatDate } from '@/utils/formatDate';
import { useRegistersMutation } from '@/components/project-governance/useGovernanceMutation';

export function CheckpointReportForm({
  projectId,
  milestoneId,
  canAdd,
}: {
  projectId: string;
  milestoneId: string;
  canAdd: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: reports } = useQuery({
    queryKey: ['projectCheckpoints', projectId, milestoneId],
    queryFn: () => projectGovernanceApi.listCheckpoints(projectId, milestoneId),
  });
  const schema = yup
    .object({
      progressPct: yup.number().min(0).max(100).required(t('errors.required')),
      forecastDueDate: yup.string().default(''),
      issues: yup.string().default(''),
      nextSteps: yup.string().default(''),
    })
    .required();
  const { register, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { progressPct: 0, forecastDueDate: '', issues: '', nextSteps: '' },
  });
  const mutation = useRegistersMutation(
    projectId,
    (dto: Parameters<typeof projectGovernanceApi.createCheckpoint>[2]) =>
      projectGovernanceApi.createCheckpoint(projectId, milestoneId, dto),
    () => {
      reset();
      queryClient.invalidateQueries({ queryKey: ['projectCheckpoints', projectId, milestoneId] });
    },
  );
  const latest = reports?.[0];

  return (
    <div className="border-t pt-3 mt-3 space-y-3">
      <h4 className="text-xs font-medium">{t('workspace.checkpoint.title')}</h4>
      {latest && (
        <p className="text-xs text-muted-foreground">
          {t('workspace.checkpoint.latest', {
            progress: latest.progressPct,
            date: latest.forecastDueDate ? formatDate(latest.forecastDueDate) : t('generic.unspecified'),
            at: formatDate(latest.createdAt),
          })}
        </p>
      )}
      {canAdd && (
        <form
          className="grid gap-2"
          onSubmit={handleSubmit((v) => {
            mutation.mutate({
              progressPct: v.progressPct,
              forecastDueDate: v.forecastDueDate || null,
              issues: v.issues || null,
              nextSteps: v.nextSteps || null,
            });
          })}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t('workspace.checkpoint.progress')}</Label>
              <Input type="number" min={0} max={100} {...register('progressPct')} />
            </div>
            <div>
              <Label className="text-xs">{t('workspace.checkpoint.forecast')}</Label>
              <Input type="date" {...register('forecastDueDate')} />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('workspace.checkpoint.issues')}</Label>
            <Textarea className="min-h-16" {...register('issues')} />
          </div>
          <div>
            <Label className="text-xs">{t('workspace.checkpoint.nextSteps')}</Label>
            <Textarea className="min-h-16" {...register('nextSteps')} />
          </div>
          <Button type="submit" size="sm" disabled={mutation.isPending}>
            {t('workspace.checkpoint.save')}
          </Button>
        </form>
      )}
    </div>
  );
}
